import type { Actor } from '../../sim/actor';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { scaleShape } from '../../sim/shapes';
import type { Mod } from '../../sim/skills';
import { T, forEachHit, tripod } from '../../sim/skills';
import type { ResolvedSkill, SkillAction, SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import { applyStatus, removeStatus } from '../../sim/statusSystem';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, cone, dashSkill, deg, hit, hits, identityOp, invuln, move, proj, rect, self, skill, stage } from './kit';

/**
 * FIENDBINDER — demon-pact assassin (transformation DPS archetype).
 * Hooked twin blades and a bound fiend's arm. Infernal skills feed Corruption;
 * at 100 the Fiendbinder Unbinds into a fiend with a whole new skill bar, its
 * own dash and no mana costs, until the Corruption drains away or is spent on
 * a Ruinous Rend.
 */

const HUE = 0xc040ff;
const FIEND_HUE = 0xff2a5c;
const APEX_HUE = 0x9a30ff;

const HUMAN_MODEL = 'hero_assassin';
const FIEND_MODEL = 'hero_fiend';
/** `requires` values: skills usable in one form only. */
const HUMAN = 'human';
const FIEND = 'fiend';
/** Skill tags read by the identity. */
const INFERNAL = 'infernal';
const SPENDER = 'spender';

const GAUGE_MAX = 100;
/** Corruption drained per second in Fiend Form (100 → 0 in 20 s). */
const DRAIN = 5;
const FORM_TIME = GAUGE_MAX / DRAIN;
const TRANSFORM_TIME = 0.6;
/** Ruinous Rend: +3% damage per remaining Corruption. */
const RUIN_PER_POINT = 0.03;
/** "Consume" facet: spends 20 Corruption for +240% damage. */
const SPEND_COST = 20;
const SPEND_BONUS = 2.4;

const FORM_STATUS = 'fnd_fiend';
const UNBIND = 'fnd_unbind';
const RUIN = 'fnd_ruin';
const RIFT_STEP = 'fnd_rift';

registerStatus({
  id: FORM_STATUS, name: 'Fiend Form', kind: 'buff', icon: FORM_STATUS,
  description: 'Unbound: move speed +20%, crit rate +30%, damage +10% (no positional bonuses), paralysis immunity, no mana costs.',
  stacking: 'refresh', mods: { moveSpeedPct: 0.2, critRate: 0.3, dmgPct: 0.1 }, color: FIEND_HUE,
});

// ------------------------------------------------------------------ human skills

const twinCrescent = skill({
  id: 'fnd_crescent', name: 'Twin Crescent', hue: HUE,
  description: 'A downward cut, then a rising crescent with the hooked blades. Counters blue-glowing enemies.',
  castType: 'normal', cooldown: 8, mana: 50, positional: 'back', counter: true, weakPoint: 1,
  stages: [stage('Attack2', 0.55, [
    hit(0.14, cone(3.5, 120), 1.4, { reaction: { kind: 'flinch' }, weakPoint: 0 }),
    hit(0.34, cone(3.5, 120), 1.8, { reaction: { kind: 'knockback', power: 1 } }),
  ])],
});

const fiendsGrasp = skill({
  id: 'fnd_grasp', name: "Fiend's Grasp", hue: HUE,
  description: "The bound fiend's arm lances 7 m forward and drags foes back to you. Infernal: +20 Corruption on hit.",
  castType: 'normal', cooldown: 18, mana: 85, stagger: 'low', tags: [INFERNAL], identityGain: 20,
  stages: [stage('Cast', 0.6, [hit(0.28, rect(7, 2), 4.8, { reaction: { kind: 'pull', power: 3.5 } })])],
});

const hellgaze = skill({
  id: 'fnd_gaze', name: 'Hellgaze', hue: HUE,
  description: "Charge, then open the fiend's eye: a 10 m ray that sears everything in its path and hurls foes back. Two charge levels. Infernal: +25 Corruption on hit.",
  castType: 'charge', cooldown: 24, mana: 100, superArmor: 'paralysis', stagger: 'midHigh', tags: [INFERNAL], identityGain: 25,
  charge: { levels: [0.5, 1.0], mults: [0.3, 0.5, 1.0], anim: 'Charge' },
  stages: [stage('Channel', 0.85, [
    hits(0.08, 5, 0.12, rect(10, 2.5), 7, { reaction: { kind: 'flinch' } }),
    hit(0.7, rect(10, 2.5), 3, { reaction: { kind: 'knockback', power: 2.5 }, stagger: 'none', vfx: 'heavy' }),
  ])],
});

const rendingDash = skill({
  id: 'fnd_rending', name: 'Rending Dash', hue: HUE,
  description: 'Dash 6 m through foes with three hooked cuts, then finish with a wide swing. Enemies hit are Exposed (+6% damage taken) for 8 s.',
  castType: 'normal', cooldown: 10, mana: 60, positional: 'back', superArmor: 'paralysis', weakPoint: 1,
  synergy: { id: 'exposed', duration: 8 },
  stages: [stage('Dash', 0.7, [
    move(0, 6, 0.3),
    hits(0.03, 3, 0.1, rect(2.2, 2, -0.6), 2, { reaction: { kind: 'flinch' }, weakPoint: 0 }),
    hit(0.42, cone(3.2, 160), 2.5, { reaction: { kind: 'knockback', power: 1.5 } }),
  ])],
});

const thornField = skill({
  id: 'fnd_thorns', name: 'Thorn Field', hue: HUE,
  description: 'Hell-thorns erupt at the target location (up to 12 m), knocking foes down. Infernal: +20 Corruption on hit.',
  castType: 'point', cooldown: 20, mana: 90, maxRange: 12, stagger: 'mid', tags: [INFERNAL], identityGain: 20,
  stages: [stage('Slam', 0.6, [
    area(0.15, { shape: circle(4), anchor: 'point', delay: 0.3, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(4), coef: 6.4, reaction: { kind: 'knockdown', duration: 1.2 } } }),
  ])],
});

const chainReaper = skill({
  id: 'fnd_chain', name: 'Chain Reaper', hue: HUE,
  description: 'Hurl a chained hook-blade 9 m: it rips through every foe on the way out and again on the way back. You cannot act until it returns.',
  castType: 'normal', cooldown: 12, mana: 65, stagger: 'mid',
  stages: [stage('Attack1', 1.1, [
    proj(0.15, { speed: 20, range: 9, radius: 1, pierce: true, returns: true, height: 1, hit: { shape: circle(1), coef: 3, stagger: 'low', reaction: { kind: 'flinch' } } }),
  ], { cancelAt: 1.05 })],
});

const netherSpin = skill({
  id: 'fnd_spin', name: 'Nether Spin', hue: HUE,
  description: 'Become a whirl of blades and spin 9 m forward, passing through enemies.',
  castType: 'normal', cooldown: 18, mana: 85, superArmor: 'paralysis', stagger: 'low',
  stages: [stage('Spin', 0.7, [
    move(0, 9, 0.6, { ignoreCollision: true }),
    hits(0.05, 6, 0.1, circle(1.8), 6, { reaction: { kind: 'flinch' } }),
  ])],
});

const summonedTalons = skill({
  id: 'fnd_talons', name: 'Summoned Talons', hue: HUE,
  description: 'Giant fiend arms tear through a wide arc in front of you, then slam foes to the ground. Infernal: +30 Corruption on hit.',
  castType: 'normal', cooldown: 27, mana: 105, superArmor: 'paralysis', tags: [INFERNAL], identityGain: 30,
  stages: [stage('Cast', 0.85, [
    hit(0.3, cone(6, 120), 3.5, { reaction: { kind: 'flinch' } }),
    hit(0.58, cone(6, 120), 4.5, { reaction: { kind: 'knockdown', duration: 1, power: 1 }, vfx: 'heavy' }),
  ])],
});

const gateOfTorment = skill({
  id: 'fnd_gate', name: 'Gate of Torment', hue: APEX_HUE,
  description: 'APEX ART. Tear open a hell-gate at the target location (up to 12 m). Its scream terrifies monsters for 3 s, its maw drags everything nearby in for 2 s, then it detonates. Usable in Fiend Form.',
  castType: 'point', cooldown: 300, mana: 0, kind: 'awakening', maxRange: 12, superArmor: 'full', stagger: 'max', weakPoint: 2,
  stages: [stage('Cast', 1.0, [
    invuln(0, 1.0),
    area(0.25, { shape: circle(6), anchor: 'point', delay: 0, telegraph: 'none', hit: { shape: circle(6), coef: 1, status: [{ id: 'fear', duration: 3 }], stagger: 'none', weakPoint: 0 } }),
    area(0.25, { shape: circle(6), anchor: 'point', delay: 0.05, duration: 2, tickInterval: 0.25, pull: 5, telegraph: 'none', hit: { shape: circle(6), coef: 0.6, stagger: 'none', weakPoint: 0 } }),
    area(0.25, { shape: circle(6.5), anchor: 'point', delay: 2.1, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(6.5), coef: 24, reaction: { kind: 'launch', power: 1, duration: 1.2 }, vfx: 'heavy' } }),
  ])],
});

const shadowSlide: SkillDef = {
  ...dashSkill('fnd_dash', 'Shadow Slide', HUE, 8, 6),
  description: 'Slide 6 m through the shadows toward the cursor. Immune to push and crowd control; briefly invulnerable.',
  requires: HUMAN,
};

// ------------------------------------------------------------------ identity skills (Z / X) and the Fiend Form bar

const unbind = skill({
  id: UNBIND, name: 'Unbind', hue: FIEND_HUE,
  description: 'At 100 Corruption: break the seal. After a 0.6 s transformation (fully immune) that repels nearby foes, become a fiend for up to 20 s.',
  castType: 'normal', cooldown: 1, mana: 0, kind: 'identity', superArmor: 'full',
  stages: [stage('Buff', TRANSFORM_TIME, [
    invuln(0, TRANSFORM_TIME),
    hit(0.4, circle(4), 0.5, { reaction: { kind: 'knockback', power: 3 } }),
    area(0.4, { shape: circle(4), delay: 0, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(4), coef: 0 } }),
    identityOp(TRANSFORM_TIME - 0.02, 'unbind'),
  ])],
});

const ruinousRend = skill({
  id: RUIN, name: 'Ruinous Rend', hue: FIEND_HUE,
  description: 'Fiend Form only. Rip an 8 m swath open and end the form early. +3% damage per remaining Corruption.',
  castType: 'normal', cooldown: 1, mana: 0, kind: 'identity', requires: FIEND, superArmor: 'push', stagger: 'max', weakPoint: 2,
  stages: [stage('Slam', 0.7, [
    hit(0.28, rect(8, 3), 4, { reaction: { kind: 'flinch' }, stagger: 'none', weakPoint: 0 }),
    hit(0.55, rect(8, 3), 8, { reaction: { kind: 'knockdown', duration: 1.4, power: 2 }, vfx: 'heavy' }),
    // Same tick as the final blow, so a dash can't cancel the form's end.
    identityOp(0.55, 'endForm'),
  ])],
});

const riftStep: SkillDef = {
  ...dashSkill(RIFT_STEP, 'Rift Step', FIEND_HUE, 7, 6, { teleport: true }),
  description: 'Fiend Form: vanish into the rift and reappear 6 m toward the cursor. Immune to push and crowd control; briefly invulnerable.',
  requires: FIEND,
};

type FiendInput = Pick<SkillDef, 'id' | 'name' | 'description' | 'cooldown' | 'stages'> & Partial<SkillDef>;

/** Fiend Form skills: no mana, no tripods, no positional tags (the form grants flat damage instead). */
const fiendSkill = (s: FiendInput): SkillDef => skill({ castType: 'normal', mana: 0, kind: 'identity', requires: FIEND, hue: FIEND_HUE, ...s });

const maul = fiendSkill({
  id: 'fnd_maul', name: 'Maul', cooldown: 4, stagger: 'low', counter: true,
  description: 'A quick claw followed by a crushing high blow. Counters blue-glowing enemies.',
  stages: [stage('Attack1', 0.55, [
    hit(0.12, cone(3.5, 120), 3, { reaction: { kind: 'flinch' }, stagger: 'none' }),
    hit(0.34, cone(3.5, 120), 6, { reaction: { kind: 'knockback', power: 1.5 }, vfx: 'heavy' }),
  ])],
});

const rendingClaw = fiendSkill({
  id: 'fnd_claw', name: 'Rending Claw', cooldown: 6, stagger: 'low',
  description: 'Lunge 5 m and rake with the claws. Enemies hit are Exposed (+6% damage taken) for 6 s.',
  synergy: { id: 'exposed', duration: 6 },
  stages: [stage('Dash', 0.6, [
    move(0, 5, 0.25),
    hits(0.02, 2, 0.1, rect(2.2, 2.2, -0.6), 2.5, { reaction: { kind: 'flinch' }, stagger: 'none' }),
    hit(0.3, cone(3.2, 140), 6.5, { reaction: { kind: 'knockback', power: 1.2 } }),
  ])],
});

const carnageWave = fiendSkill({
  id: 'fnd_carnage', name: 'Carnage Wave', cooldown: 10, stagger: 'midHigh', weakPoint: 1,
  description: 'Advance behind a frenzy of claws that shreds everything up to 5 m ahead.',
  stages: [stage('Attack2', 0.8, [
    move(0, 2.5, 0.55),
    hits(0.08, 4, 0.14, cone(5, 90), 8, { reaction: { kind: 'flinch' } }),
  ])],
});

const pounce = fiendSkill({
  id: 'fnd_pounce', name: 'Pounce', castType: 'point', maxRange: 10, cooldown: 16, stagger: 'midHigh', weakPoint: 1, superArmor: 'push',
  description: 'Leap to the target location (up to 10 m) and crash down in a 4 m shockwave that causes Earthquake for 2 s.',
  stages: [stage('Leap', 0.75, [
    move(0, 10, 0.35, { toPoint: true, ignoreCollision: true }),
    hit(0.4, circle(4), 7, { status: [{ id: 'quake', duration: 2 }], vfx: 'heavy' }),
  ])],
});

const bloodNova = fiendSkill({
  id: 'fnd_nova', name: 'Blood Nova', cooldown: 16, stagger: 'high',
  description: 'Detonate the blood within you, blasting everything within 5 m.',
  stages: [stage('Roar', 0.8, [
    hit(0.42, circle(5), 11, { reaction: { kind: 'knockback', power: 3 }, vfx: 'heavy' }),
    area(0.42, { shape: circle(5), delay: 0, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(5), coef: 0 } }),
  ])],
});

const impalingSurge = fiendSkill({
  id: 'fnd_surge', name: 'Impaling Surge', cooldown: 60, stagger: 'midHigh',
  description: 'Drive a blood lance 8 m forward; it spins where it strikes, shredding everything along its length.',
  stages: [stage('Attack3', 1.25, [
    hit(0.25, rect(8, 2), 10, { reaction: { kind: 'flinch' }, stagger: 'none' }),
    hits(0.45, 6, 0.12, rect(8, 3), 16, { reaction: { kind: 'flinch' } }),
  ])],
});

/** Fiend Form skill bar: Q W E R A S (D and F stay empty). */
const FIEND_BAR: readonly string[] = [maul.id, rendingClaw.id, carnageWave.id, pounce.id, bloodNova.id, impalingSurge.id];

// ------------------------------------------------------------------ tripod (facet) helpers

const actionsOf = (s: ResolvedSkill): SkillAction[] => s.stages.flatMap((st) => st.actions);

/** Adds a fresh copy of `action` to a stage (copies keep facets from sharing mutable data). */
const add = (action: SkillAction, stageIdx = 0): Mod => (s) => T.addAction(structuredClone(action), stageIdx)(s);

/** Area +pct: hit shapes plus ground-effect visuals and projectile bodies. */
const aoe = (pct: number): Mod => (s) => {
  T.aoe(pct)(s);
  const k = 1 + pct;
  for (const a of actionsOf(s)) {
    if (a.kind === 'area') a.area.shape = a.area.shape.kind === 'rect' ? { ...a.area.shape, width: a.area.shape.width * k } : scaleShape(a.area.shape, k);
    else if (a.kind === 'projectile') a.proj.radius *= k;
  }
};

/** "Unfed" facets: the skill stops feeding Corruption. */
const unfed: Mod = (s) => {
  s.identityGainMult = 0;
};

const addTag = (tag: string): Mod => (s) => {
  s.tags = [...(s.tags ?? []), tag];
};

const quicken = add(self(0, { id: 'haste', duration: 5, value: 0.2 }));
const shackle = (duration: number): Mod[] => [T.status({ id: 'weaken', duration, value: -0.2 }), T.status({ id: 'slow', duration, value: -0.2 })];
const bleed5 = T.status({ id: 'bleed', duration: 5, value: 0.2 });

/** Stretches every multi-hit and timed move of the main stages by `extra` seconds (the hit rate stays the same). */
const lengthen = (extra: number, interval: number, coefMult = 1): Mod => (s) => {
  for (const st of s.stages) {
    st.duration += extra;
    for (const a of st.actions) {
      if (a.kind === 'move') a.duration += extra;
      if (a.kind === 'hit' && (a.hit.count ?? 1) > 1) {
        a.hit.count = (a.hit.count ?? 1) + Math.round(extra / interval);
        a.hit.coef *= coefMult;
      }
    }
  }
};

const tripods: ClassDef['tripods'] = {
  fnd_crescent: [
    tripod('fnd_crescent_t1a', 1, 'Deadly', 'Crit rate +45%.', T.crit(0.45)),
    tripod('fnd_crescent_t1b', 1, 'Amplify', 'Enemies hit are Exposed (+6% damage taken) for 8 s.', T.synergy({ id: 'exposed', duration: 8 })),
    tripod('fnd_crescent_t1c', 1, 'Swift', 'Attack speed +27%.', T.atkSpeed(0.27)),
    tripod('fnd_crescent_t2a', 2, 'Quicken', 'Gain +20% move speed for 5 s.', quicken),
    tripod('fnd_crescent_t2b', 2, 'Opening Stab', 'Open with a quick stab for an extra 70% damage.', add(hit(0.05, rect(3.2, 1.4), 0.7 * 3.2, { reaction: { kind: 'flinch' }, weakPoint: 0 }))),
    tripod('fnd_crescent_t2c', 2, 'Keen', 'Crit damage +160%.', T.critDmg(1.6)),
    tripod('fnd_crescent_t3a', 3, 'Rising Moon', 'The rising crescent launches foes. Area +20%, damage +71%.', T.custom((s) => {
      const last = actionsOf(s).filter((a) => a.kind === 'hit').pop();
      if (last?.kind === 'hit') last.hit.reaction = { kind: 'launch', power: 0.5, duration: 0.9 };
    }), aoe(0.2), T.dmg(0.71)),
    tripod('fnd_crescent_t3b', 3, 'Storm Blades', 'Lightning blades: hits Shock for 2 s (80% attack per second). Cooldown +6 s.', T.element('lightning', { id: 'shock', duration: 2, value: 0.8 }), T.cd(6)),
  ],
  fnd_grasp: [
    tripod('fnd_grasp_t1a', 1, 'Deadly', 'Crit rate +45%.', T.crit(0.45)),
    tripod('fnd_grasp_t1b', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('fnd_grasp_t1c', 1, 'Swift', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('fnd_grasp_t2a', 2, 'Shackle', 'Enemies hit deal 20% less damage and move 20% slower for 2 s.', ...shackle(2)),
    tripod('fnd_grasp_t2b', 2, 'Hungry Grip', 'Corruption gain +50%.', T.identityGain(0.5)),
    tripod('fnd_grasp_t2c', 2, 'Pierce', 'Ignore 40% of defence.', T.defPen(0.4)),
    tripod('fnd_grasp_t3a', 3, 'Giant Grasp', 'Width +50%. Damage +70%.', aoe(0.5), T.dmg(0.7)),
    tripod('fnd_grasp_t3b', 3, 'Hurl', 'Knock foes back instead of pulling them. Damage +80% to push-immune foes.', T.reaction({ kind: 'knockback', power: 3 }), T.vsBoss(0.8)),
  ],
  fnd_gaze: [
    tripod('fnd_gaze_t1a', 1, 'Quick Release', 'Charge levels are reached 50% faster.', T.custom((s) => {
      const ch = s.charge;
      if (!ch) return;
      ch.levels = ch.levels.map((t) => t * 0.5);
      if (ch.overcharge) ch.overcharge = [ch.overcharge[0] * 0.5, ch.overcharge[1] * 0.5];
    })),
    tripod('fnd_gaze_t1b', 1, 'Long Stare', 'The ray lasts 50% longer. Damage +59%.', T.custom((s) => {
      for (const st of s.stages) {
        st.duration *= 1.5;
        for (const a of st.actions) {
          if (a.kind === 'hit' && (a.hit.count ?? 1) > 1) a.hit.count = Math.round((a.hit.count ?? 1) * 1.5);
          else a.at *= 1.5;
        }
      }
    }), T.dmg(0.59)),
    tripod('fnd_gaze_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('fnd_gaze_t2a', 2, 'Storm Gaze', 'Lightning ray: no knock-back; hits Shock for 3 s.', T.element('lightning', { id: 'shock', duration: 3, value: 0.3 }), T.custom((s) => {
      forEachHit(s, (h) => {
        if (h.reaction?.kind === 'knockback') h.reaction = { kind: 'flinch' };
      });
    })),
    tripod('fnd_gaze_t2b', 2, 'Void Gaze', 'Dark ray. Crit rate +50%.', T.element('dark'), T.crit(0.5)),
    tripod('fnd_gaze_t2c', 2, 'Hungry Gaze', 'Corruption gain +50%.', T.identityGain(0.5)),
    tripod('fnd_gaze_t3a', 3, 'Snap Gaze', 'No charging: the ray fires instantly at full strength.', T.custom((s) => {
      s.castType = 'normal';
      s.charge = undefined;
    })),
    tripod('fnd_gaze_t3b', 3, 'Overcharge', 'Adds a third charge level and an overcharge window: an overcharged ray deals +120% damage and is 20% larger.', T.custom((s) => {
      const ch = s.charge;
      if (!ch) return;
      const last = ch.levels[ch.levels.length - 1] ?? 1;
      const step = last - (ch.levels[ch.levels.length - 2] ?? 0);
      ch.levels = [...ch.levels, last + step];
      ch.mults = [...ch.mults, 1.3];
      ch.overcharge = [last + step, last + step * 1.6];
      ch.overMult = 2.2;
      ch.overAreaScale = 1.2;
    })),
  ],
  fnd_rending: [
    tripod('fnd_rending_t1a', 1, 'Amplify', 'Exposed lasts 16 s.', T.synergy({ id: 'exposed', duration: 16 })),
    tripod('fnd_rending_t1b', 1, 'Evasive', 'Take 10% less damage while dashing.', T.damageReduction(0.1)),
    tripod('fnd_rending_t1c', 1, 'Focus', 'Damage +45%.', T.dmg(0.45)),
    tripod('fnd_rending_t2a', 2, 'Wide Rend', 'Area +20%.', aoe(0.2)),
    tripod('fnd_rending_t2b', 2, 'Shackle', 'Enemies hit deal 20% less damage and move 20% slower for 3 s.', ...shackle(3)),
    tripod('fnd_rending_t2c', 2, 'Quicken', 'Gain +20% move speed for 5 s.', quicken),
    tripod('fnd_rending_t3a', 3, 'Chain Dash', 'Can be used twice in a row (chain). Cooldown +3 s.', T.custom((s) => {
      const st = s.stages[0];
      if (!st) return;
      s.castType = 'chain';
      s.stages = [st, structuredClone(st)];
      s.followWindow = 2.5;
      s.chainPenalty = 0;
    }), T.cd(3)),
    tripod('fnd_rending_t3b', 3, 'Phantom Dash', 'Dash 4 m farther in the same time, passing through enemies.', T.range(4), T.custom((s) => {
      for (const a of actionsOf(s)) if (a.kind === 'move') a.ignoreCollision = true;
    })),
  ],
  fnd_thorns: [
    tripod('fnd_thorns_t1a', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('fnd_thorns_t1b', 1, 'Bleeding Thorns', 'Hits cause Bleed for 5 s (20% attack per second).', bleed5),
    tripod('fnd_thorns_t1c', 1, 'Deadly', 'Crit rate +45%.', T.crit(0.45)),
    tripod('fnd_thorns_t2a', 2, 'Rot Zone', 'The thorns rot into a field for 4 s, dealing up to 80% more damage.', T.custom((s) => {
      for (const st of s.stages) {
        const base = st.actions.find((a) => a.kind === 'area');
        if (base?.kind !== 'area') continue;
        const b = base.area;
        st.actions.push(area(base.at, {
          shape: b.shape, anchor: b.anchor, delay: (b.delay ?? 0) + 0.5, duration: 3.75, tickInterval: 0.5, telegraph: 'none',
          hit: { shape: b.hit.shape, coef: (b.hit.coef * 0.8) / 8, stagger: 'none', element: b.hit.element },
        }));
      }
    })),
    tripod('fnd_thorns_t2b', 2, 'Unfed Thorns', 'No longer builds Corruption. Damage +160%.', unfed, T.dmg(1.6)),
    tripod('fnd_thorns_t2c', 2, 'Quick Prep', 'Cooldown -8 s.', T.cd(-8)),
    tripod('fnd_thorns_t3a', 3, 'Thorn Lance', 'Becomes an instant 8 m line of thorns erupting in front of you. Damage +118%.', T.custom((s) => {
      s.castType = 'normal';
      s.maxRange = undefined;
      for (const a of actionsOf(s)) {
        if (a.kind !== 'area') continue;
        a.area.anchor = 'self';
        a.area.shape = rect(8, 2.5);
        a.area.hit = { ...a.area.hit, shape: rect(8, 2.5) };
      }
    }), T.dmg(1.18)),
    tripod('fnd_thorns_t3b', 3, 'Great Thorns', 'Area +50%. Damage +80%.', aoe(0.5), T.dmg(0.8)),
  ],
  fnd_chain: [
    tripod('fnd_chain_t1a', 1, 'Keen', 'Crit damage +90%.', T.critDmg(0.9)),
    tripod('fnd_chain_t1b', 1, 'Earth Chain', 'Earth element; the blade is 20% larger.', T.element('earth'), aoe(0.2)),
    tripod('fnd_chain_t1c', 1, 'Evasive', 'Take 10% less damage while the blade is out.', T.damageReduction(0.1)),
    tripod('fnd_chain_t2a', 2, 'Deadly', 'Crit rate +60%.', T.crit(0.6)),
    tripod('fnd_chain_t2b', 2, 'Hobble', 'Enemies hit are slowed by 50% for 2 s.', T.status({ id: 'slow', duration: 2, value: -0.5 })),
    tripod('fnd_chain_t2c', 2, 'Double Throw', 'Throw a second blade right after the first for an extra 92% damage.', T.custom((s) => {
      for (const st of s.stages) {
        const first = st.actions.find((a) => a.kind === 'projectile');
        if (first?.kind !== 'projectile') continue;
        const second = structuredClone(first);
        second.at += 0.3;
        second.proj.hit.coef *= 0.92;
        st.actions.push(second);
        st.actions.sort((x, y) => x.at - y.at);
        st.duration += 0.3;
        if (st.cancelAt !== undefined) st.cancelAt += 0.3;
      }
    })),
    tripod('fnd_chain_t3a', 3, 'Consume', `Spends ${SPEND_COST} Corruption (when available) for +240% damage. Cooldown +8 s.`, addTag(SPENDER), T.cd(8)),
    tripod('fnd_chain_t3b', 3, 'Reaper Zone', 'Becomes a point skill: the blade spins at the target location (up to 9 m) for 3 s. Damage +120% to normal and elite monsters.', T.custom((s) => {
      s.castType = 'point';
      s.maxRange = 9;
      for (const st of s.stages) {
        // Out-and-back (2 passes) becomes 10 spinning ticks for the same total; the zone keeps any blade size facet.
        st.actions = st.actions.map((a) => {
          if (a.kind !== 'projectile') return a;
          const zone = circle(2.5 * a.proj.radius);
          return area(a.at, { shape: zone, anchor: 'point', delay: 0.35, duration: 2.85, tickInterval: 0.3, telegraph: 'none', hit: { ...a.proj.hit, shape: zone, coef: (a.proj.hit.coef * 2) / 10, stagger: 'none' } });
        });
        st.duration = Math.max(0.55, ...st.actions.map((a) => a.at + 0.1));
        st.cancelAt = undefined;
      }
    }), T.vsWeak(1.2)),
  ],
  fnd_spin: [
    tripod('fnd_spin_t1a', 1, 'Long Spin', 'Spin 3 m farther.', T.range(3), lengthen(0.2, 0.1)),
    tripod('fnd_spin_t1b', 1, 'Sustain', 'Spin 0.4 s longer for up to 46% more damage.', lengthen(0.4, 0.1, 1.46)),
    tripod('fnd_spin_t1c', 1, 'Grounded', 'Stop at walls instead of passing through. Damage +45%.', T.custom((s) => {
      for (const a of actionsOf(s)) if (a.kind === 'move') a.ignoreCollision = false;
    }), T.dmg(0.45)),
    tripod('fnd_spin_t2a', 2, 'Bleed', 'Hits cause Bleed for 5 s (20% attack per second).', bleed5),
    tripod('fnd_spin_t2b', 2, 'Vertical Spin', 'Area -60%. Damage +95%.', aoe(-0.6), T.dmg(0.95)),
    tripod('fnd_spin_t2c', 2, 'Lunge', 'First lunge up to 6 m toward the cursor.', T.custom((s) => {
      s.preStage = stage('Dash', 0.25, [move(0, 6, 0.22, { toPoint: true })]);
    })),
    tripod('fnd_spin_t3a', 3, 'Critical Spin', 'Crit rate +30% and crit damage +30%.', T.crit(0.3), T.critDmg(0.3)),
    tripod('fnd_spin_t3b', 3, 'Fiend Spin', 'Damage +60% to push-immune foes.', T.vsBoss(0.6)),
  ],
  fnd_talons: [
    tripod('fnd_talons_t1a', 1, 'Bleed', 'Hits cause Bleed for 5 s (20% attack per second).', bleed5),
    tripod('fnd_talons_t1b', 1, 'Swift', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('fnd_talons_t1c', 1, 'Quick Prep', 'Cooldown -9 s.', T.cd(-9)),
    tripod('fnd_talons_t2a', 2, 'Bosskiller', 'Damage +60% to push-immune foes.', T.vsBoss(0.6)),
    tripod('fnd_talons_t2b', 2, 'Unfed Talons', 'No longer builds Corruption. Damage +145%.', unfed, T.dmg(1.45)),
    tripod('fnd_talons_t2c', 2, 'Keen', 'Crit damage +160%.', T.critDmg(1.6)),
    tripod('fnd_talons_t3a', 3, 'Tremor', 'The ground keeps rumbling after the slam: an aftershock field deals up to 120% more damage.', add(
      area(0.62, { shape: circle(5, 3), delay: 0.15, duration: 1.1, tickInterval: 0.35, telegraph: 'none', hit: { shape: circle(5, 3), coef: (8 * 1.2) / 4, stagger: 'none', reaction: { kind: 'flinch' } } }),
    )),
    tripod('fnd_talons_t3b', 3, 'Sweeping Talons', 'Horizontal sweeps cover 200° around you. Damage +95%.', T.custom((s) => {
      forEachHit(s, (h) => {
        if (h.shape.kind === 'cone') h.shape = { ...h.shape, angle: deg(200) };
      });
    }), T.dmg(0.95)),
  ],
};

// ------------------------------------------------------------------ identity

const isFiend = (a: Actor): boolean => a.player?.identity.mode === FIEND;
const pendingKey = (skillId: string): string => `pending:${skillId}`;
const spentKey = (skillId: string): string => `spent:${skillId}`;

/** Corruption a cast will grant on its first hit (Infernal skills only). */
function corruptionGain(a: Actor, s: SkillDef): number {
  if (!s.tags?.includes(INFERNAL)) return 0;
  const facetMult = (s as Partial<ResolvedSkill>).identityGainMult ?? 1;
  return (s.identityGain ?? 0) * facetMult * a.stats.identityGainMult;
}

/** Swaps the actor's visual model and tells the presentation/net layers. */
function setModel(world: World, a: Actor, model: string): void {
  if (a.look.model === model) return;
  a.look = { ...a.look, model };
  world.emit({ type: 'look', id: a.id, model });
}

function enterFiendForm(world: World, a: Actor): void {
  const id = a.player!.identity;
  if (id.mode === FIEND) return;
  id.mode = FIEND;
  id.gauge = GAUGE_MAX;
  id.timer = FORM_TIME;
  a.superArmorBonus = 'paralysis';
  applyStatus(world, a, { id: FORM_STATUS, duration: FORM_TIME }, a.id, a.stats.attack);
  setModel(world, a, FIEND_MODEL);
}

function endFiendForm(world: World, a: Actor): void {
  const id = a.player!.identity;
  if (id.mode !== FIEND) return;
  id.mode = 'idle';
  id.gauge = 0;
  id.timer = 0;
  a.superArmorBonus = 'none';
  removeStatus(world, a, FORM_STATUS);
  setModel(world, a, HUMAN_MODEL);
}

const identity: IdentityController = {
  gaugeMax: GAUGE_MAX,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = 'idle';
    id.timer = 0;
    id.data = {};
    a.superArmorBonus = 'none';
    if (a.look.model === FIEND_MODEL) a.look = { ...a.look, model: HUMAN_MODEL };
  },
  onSkillCast(_w, a, s) {
    const id = a.player!.identity;
    if (s.tags?.includes(INFERNAL)) id.data[pendingKey(s.id)] = id.mode === FIEND ? 0 : corruptionGain(a, s);
    if (s.tags?.includes(SPENDER)) {
      const spend = id.mode !== FIEND && id.gauge >= SPEND_COST;
      if (spend) id.gauge -= SPEND_COST;
      id.data[spentKey(s.id)] = spend ? 1 : 0;
    }
    if (s.id === RUIN) id.data.ruin = id.gauge;
  },
  onDamageDealt(_w, a, s) {
    const id = a.player!.identity;
    if (!s || id.mode === FIEND) return;
    const key = pendingKey(s.id);
    const gain = id.data[key] ?? 0;
    if (gain <= 0) return;
    // Each Infernal cast feeds Corruption once, on its first hit.
    id.data[key] = 0;
    id.gauge = Math.min(GAUGE_MAX, id.gauge + gain);
  },
  onKey(world, a, key) {
    const id = a.player!.identity;
    if (key === 'Z') {
      if (id.mode === FIEND || id.gauge < GAUGE_MAX) return false;
      const s = a.skills.get(UNBIND);
      return s ? world.useSkill(a, s, 'Z') : false;
    }
    if (id.mode !== FIEND) return false;
    const s = a.skills.get(RUIN);
    return s ? world.useSkill(a, s, 'X') : false;
  },
  skillForSlot(a, slot, base) {
    return isFiend(a) ? (FIEND_BAR[slot] ?? null) : base;
  },
  dashFor(a, base) {
    return isFiend(a) ? RIFT_STEP : base;
  },
  canCast(a, s) {
    const id = a.player!.identity;
    const fiend = id.mode === FIEND;
    if (s.id === UNBIND) return !fiend && id.gauge >= GAUGE_MAX;
    // Once the Corruption runs dry only the dash still works while the form winds down.
    if (s.requires === FIEND) return fiend && (s.kind === 'dash' || id.gauge > 0);
    if (s.requires === HUMAN) return !fiend;
    // The human bar is sealed in Fiend Form (the apex art, basic attack and dash still work).
    return !(fiend && s.kind === 'skill');
  },
  op(world, a, op) {
    if (op === 'unbind') enterFiendForm(world, a);
    else if (op === 'endForm') endFiendForm(world, a);
  },
  update(world, a, dt) {
    const id = a.player!.identity;
    if (id.mode !== FIEND) return;
    const form = a.getStatus(FORM_STATUS);
    if (!form) {
      // The buff was stripped (e.g. death): revert.
      endFiendForm(world, a);
      return;
    }
    id.gauge = Math.max(0, id.gauge - DRAIN * dt);
    id.timer = id.gauge / DRAIN;
    form.remaining = Math.max(0.25, id.timer);
    // At 0 the form ends, letting a fiend skill already in motion finish first.
    const finishing = a.cast?.skill.requires === FIEND && a.cast.skill.kind !== 'dash';
    if (id.gauge <= 0 && !finishing) endFiendForm(world, a);
  },
  damageMult(a, s) {
    const id = a.player?.identity;
    if (!id) return 1;
    let m = 1;
    if (s.id === RUIN) m *= 1 + RUIN_PER_POINT * (id.data.ruin ?? id.gauge);
    if (s.tags?.includes(SPENDER) && id.data[spentKey(s.id)]) m *= 1 + SPEND_BONUS;
    return m;
  },
  describe(a) {
    const id = a.player!.identity;
    if (id.mode === FIEND) return { label: 'FIEND FORM', ready: false, active: true };
    return { label: 'Corruption', ready: id.gauge >= GAUGE_MAX, active: false };
  },
};

export const FIENDBINDER: ClassDef = {
  id: 'fiendbinder',
  name: 'Fiendbinder',
  title: 'Demon-Pact Assassin',
  archetype: 'Transformation DPS',
  role: 'dps',
  description: 'A hooked-blade assassin with a fiend caged in their own flesh. Feed it Corruption with Infernal skills, then Unbind it and fight as the fiend itself, with a whole new set of claws, leaps and blood magic.',
  hue: HUE,
  weapon: 'dagger',
  model: HUMAN_MODEL,
  baseStats: (level) => classBaseStats(level, { hp: 0.95, atk: 1.0, speed: 1.04 }),
  skills: [twinCrescent, fiendsGrasp, hellgaze, rendingDash, thornField, chainReaper, netherSpin, summonedTalons],
  defaultBar: ['fnd_crescent', 'fnd_grasp', 'fnd_gaze', 'fnd_rending', 'fnd_thorns', 'fnd_chain', 'fnd_spin', 'fnd_talons'],
  basic: basicAttack('fnd_basic', 'Hooked Blades', HUE, { radius: 2.8, angle: 120, coefs: [0.45, 0.45, 0.85] }),
  dash: shadowSlide,
  awakenings: [gateOfTorment],
  identitySkills: [unbind, ruinousRend, riftStep, maul, rendingClaw, carnageWave, pounce, bloodNova, impalingSurge],
  tripods,
  identity,
  identityName: 'Corruption',
  identityDescription: `Only Infernal skills build Corruption (+20 to +30 when they hit). At 100 press Z to Unbind: after a 0.6 s immune transformation you become a fiend for up to ${FORM_TIME} s while Corruption drains ${DRAIN} per second. The fiend moves 20% faster, crits 30% more often, deals 10% more damage (but gets no back or head bonuses), shrugs off flinches, pays no mana, uses its own six skills and blinks with Rift Step. X ends the form with a Ruinous Rend that deals +3% damage per remaining Corruption.`,
};
